import React, { ReactNode, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/context/AuthContext';

interface AuthGuardProps {
    children: ReactNode;
}

/**
 * AuthGuard component that checks if a user is authenticated and redirects to auth page if not.
 *
 * @param {ReactNode} children - Children of the AuthGuard component
 * @returns {React.FC}
 */
const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!isLoading && !isAuthenticated && !location.pathname.startsWith('/auth')) {
            navigate('/auth', { replace: true });
        }
    }, [isAuthenticated, isLoading, navigate, location.pathname]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!isAuthenticated) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return <>{children}</>;
};

export default AuthGuard;
